import React, { useState } from "react";
import styled from "styled-components";
import api from "../utils/apiClient";
import { message } from "antd";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 19, 24, 0.6);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  padding: 24px;
  animation: fadeIn 0.18s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 24px 60px rgba(15, 19, 24, 0.25);
  position: relative;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.22s cubic-bezier(0.2, 0.8, 0.2, 1);

  @keyframes slideIn {
    from { transform: translateY(12px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const Header = styled.div`
  padding: 22px 24px 14px;
  border-bottom: 1px solid #eef0f3;
`;

const Title = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.25;
`;

const Subtitle = styled.p`
  font-size: 0.88rem;
  font-weight: 500;
  color: #6b7280;
  margin: 4px 0 0;
`;

const Body = styled.div`
  padding: 20px 24px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  color: #4b5563;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s, transform 0.15s;
  z-index: 2;
  line-height: 1;

  &:hover {
    background: #db3a34;
    color: #ffffff;
    transform: scale(1.05);
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.74rem;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const InputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputPrefix = styled.span`
  position: absolute;
  left: 14px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #6b7280;
  pointer-events: none;
`;

const Input = styled.input`
  width: 100%;
  padding: 11px 14px 11px 36px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  background: #ffffff;
  color: #1f2937;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #db3a34;
    box-shadow: 0 0 0 3px rgba(219, 58, 52, 0.15);
  }
`;

const RemainingCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e7eaee;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const RemainingLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const RemainingValue = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: #db3a34;
  letter-spacing: -0.01em;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`;

const ConfirmButton = styled.button`
  background: #db3a34;
  color: white;
  border: none;
  padding: 11px 20px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  font-family: inherit;

  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &:hover {
    background: #b62e29;
  }

  &:active {
    transform: scale(0.99);
  }
`;

const CancelButton = styled.button`
  background: transparent;
  color: #b62e29;
  border: 1px solid #f0c2bf;
  padding: 11px 20px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  font-family: inherit;

  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &:hover {
    background: #fef2f1;
    border-color: #db3a34;
    color: #b62e29;
  }
`;

function formatNumberWithCommas(number) {
  return Number(number || 0)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const AbonoModal = ({ show, carro, onClose, onConfirm, ventaId }) => {
  const [input, setInput] = useState("");
  const [valueRemaining, setValueRemaining] = useState(carro.precio);

  const handleChange = (event) => {
    const value = event.target.value.replace(/[^0-9.]/g, "");
    setInput(value);
  };

  const handleConfirm = async () => {
    const dataventa = {
      carroID: carro._id,
    };
    const res = await api.post("ventas/comprabycar", dataventa);
    const ventaidentifier = res.data[0]._id;
    const data = {
      abono: input,
      identifierVenta: ventaidentifier,
      comprobantes: [],
      notiID: "",
    };
    try {
      await api.post("ventas/abono/", data);
      const newValueRemaining = valueRemaining - parseFloat(input);
      setValueRemaining(newValueRemaining);
      message.success("Abono registrado exitosamente");
      onClose();
    } catch (error) {
      console.error("Error al registrar el abono:", error);
      message.error("No se pudo registrar el abono");
    }
  };

  const handleCancel = async () => {
    const dataventa = {
      carroID: carro._id,
    };
    const res = await api.post("ventas/comprabycar", dataventa);
    const ventaidentifier = res.data[0]._id;
    const data = {
      identifierVenta: ventaidentifier,
      identifierCarro: carro._id,
    };
    try {
      await api.post("findspecial/cancelar/", data);
      message.success("cancelacion registrada exitosamente");
      onClose();
    } catch (error) {
      console.error("Error al registrar el abono:", error);
      message.error("No se pudo registrar el abono");
    }
  };

  if (!show) {
    return null;
  }

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Cerrar">×</CloseButton>
        <Header>
          <Title>Añadir solicitud de abono</Title>
          <Subtitle>
            {carro.marca} {carro.modelo} {carro.anio}
          </Subtitle>
        </Header>
        <Body>
          <Field>
            <Label htmlFor="abono-input">Monto del abono</Label>
            <InputWrap>
              <InputPrefix>L.</InputPrefix>
              <Input
                type="text"
                id="abono-input"
                value={input}
                onChange={handleChange}
                placeholder="0"
                inputMode="decimal"
              />
            </InputWrap>
          </Field>
          <RemainingCard>
            <RemainingLabel>Valor restante</RemainingLabel>
            <RemainingValue translate="no">
              L.{formatNumberWithCommas(valueRemaining)}
            </RemainingValue>
          </RemainingCard>
          <ButtonRow>
            <ConfirmButton onClick={handleConfirm} disabled={!input}>
              Confirmar
            </ConfirmButton>
            <CancelButton onClick={handleCancel}>Cancelar Venta</CancelButton>
          </ButtonRow>
        </Body>
      </ModalContainer>
    </Overlay>
  );
};

export default AbonoModal;
